// Obstetric Medications Database
// Organized by class: neuraxial opioids, neuraxial adjuncts, uterotonics, preeclampsia

export const obMedications = [
  // NEURAXIAL OPIOIDS
  {
    name: 'Fentanyl (Neuraxial)',
    proprietaryName: 'Sublimaze',
    class: 'neuraxial-opioid',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'μ-opioid': '+++'
    },
    onset: 'Intrathecal: 5-10 min, Epidural: 10-15 min',
    duration: 'Intrathecal: 2-4 hrs, Epidural: 2-4 hrs',
    dosing: {
      intrathecal: '10-25 mcg (typically 15-20 mcg)',
      epidural: 'Bolus: 50-100 mcg, Infusion: 50-100 mcg/hr',
      units: 'mcg'
    },
    uses: [
      'Labor analgesia (epidural or CSE)',
      'Cesarean delivery (intrathecal or epidural)',
      'Postoperative analgesia'
    ],
    considerations: [
      'Most commonly used neuraxial opioid',
      'Lipophilic - rapid onset, shorter duration',
      'Lower risk of delayed respiratory depression vs morphine',
      'May cause pruritus (20-100%), treat with low-dose naloxone',
      'Minimal motor block',
      'Can cross placenta but minimal neonatal effects at typical doses'
    ],
    bestFor: 'First-line neuraxial opioid for labor and cesarean'
  },
  {
    name: 'Morphine (Neuraxial)',
    proprietaryName: 'Duramorph',
    class: 'neuraxial-opioid',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'μ-opioid': '+++'
    },
    onset: 'Intrathecal: 30-60 min, Epidural: 30-60 min',
    duration: 'Intrathecal: 12-24 hrs, Epidural: 12-24 hrs',
    dosing: {
      intrathecal: '50-150 mcg (typically 100-150 mcg for cesarean)',
      epidural: '2-5 mg',
      units: 'mcg (IT) or mg (epidural)'
    },
    uses: [
      'Cesarean delivery (excellent post-op analgesia)',
      'Postcesarean pain management'
    ],
    considerations: [
      'Hydrophilic - slow onset, long duration',
      'Gold standard for postcesarean analgesia',
      'Risk of delayed respiratory depression (monitor 24 hrs)',
      'High pruritus rate (up to 80-100%)',
      'Nausea/vomiting common',
      'NOT recommended for labor (too long acting)',
      'Use preservative-free formulation only'
    ],
    bestFor: 'Postcesarean analgesia (12-24 hr duration)'
  },
  {
    name: 'Sufentanil (Neuraxial)',
    proprietaryName: 'Sufenta',
    class: 'neuraxial-opioid',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'μ-opioid': '+++'
    },
    onset: 'Intrathecal: 3-5 min, Epidural: 10 min',
    duration: 'Intrathecal: 2-4 hrs, Epidural: 3-5 hrs',
    dosing: {
      intrathecal: '2.5-10 mcg (typically 5 mcg)',
      epidural: '10-20 mcg bolus, 5-10 mcg/hr infusion',
      units: 'mcg'
    },
    uses: [
      'Labor analgesia (CSE technique)',
      'Cesarean delivery',
      'Excellent for walking epidurals'
    ],
    considerations: [
      '5-10x more potent than fentanyl',
      'Highly lipophilic - fastest onset of neuraxial opioids',
      'Less pruritus than morphine',
      'Minimal motor block',
      'Provides excellent analgesia with minimal local anesthetic',
      'May allow ambulation during labor'
    ],
    bestFor: 'CSE labor analgesia, especially for walking epidurals'
  },

  // NEURAXIAL ADJUNCTS
  {
    name: 'Epinephrine (Neuraxial)',
    proprietaryName: 'Adrenaline',
    class: 'neuraxial-adjunct',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'α1': '+++',
      'α2': '++',
      'β1': '+++',
      'β2': '++'
    },
    onset: '5-10 minutes',
    duration: 'Prolongs local anesthetic by 50-100%',
    dosing: {
      concentration: '1:200,000 (5 mcg/mL)',
      testDose: '15 mcg (3 mL of 1:200,000)',
      units: 'mcg/mL'
    },
    uses: [
      'Test dose for epidural placement',
      'Prolong duration of local anesthetics',
      'Decrease systemic absorption of local anesthetics',
      'Improve quality of epidural block'
    ],
    considerations: [
      'Vasoconstriction prolongs block duration',
      'May decrease uteroplacental blood flow (use cautiously)',
      'HR increase >20 bpm suggests IV placement (test dose)',
      'Can cause fetal bradycardia if intravascular',
      'Avoid in patients with uteroplacental insufficiency',
      'May worsen ischemic pain in labor'
    ],
    bestFor: 'Test dose and prolonging local anesthetic duration'
  },
  {
    name: 'Clonidine (Neuraxial)',
    proprietaryName: 'Duraclon',
    class: 'neuraxial-adjunct',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'α2': '+++'
    },
    onset: '10-20 minutes',
    duration: '3-6 hours',
    dosing: {
      intrathecal: '15-150 mcg (typically 30-75 mcg)',
      epidural: '100-900 mcg (typically 150-200 mcg)',
      units: 'mcg'
    },
    uses: [
      'Prolong duration of neuraxial anesthesia',
      'Improve quality of labor analgesia',
      'Postoperative analgesia',
      'Refractory labor pain'
    ],
    considerations: [
      'α2-agonist - provides analgesia via spinal mechanisms',
      'Prolongs sensory and motor block',
      'May cause hypotension and bradycardia (dose-dependent)',
      'Sedation common',
      'Does not cause respiratory depression',
      'Limited data in obstetrics - use cautiously',
      'May decrease uteroplacental perfusion'
    ],
    bestFor: 'Prolonging neuraxial blocks when opioids insufficient'
  },

  // UTEROTONICS
  {
    name: 'Oxytocin',
    proprietaryName: 'Pitocin',
    class: 'uterotonic',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'Oxytocin': '+++'
    },
    onset: 'IV: Immediate, IM: 3-5 min',
    duration: 'IV: 20-30 min (short half-life), IM: 30-60 min',
    dosing: {
      bolus: '0.5-3 units IV slow push (over 30-60 sec)',
      infusion: '10-40 units in 1000 mL at 125-200 mL/hr',
      im: '10 units IM (if no IV access)',
      units: 'units'
    },
    hemodynamics: {
      HR: '↑ (reflex tachycardia)',
      BP: '↓ (vasodilation)',
      CO: '↑↑',
      SVR: '↓↓'
    },
    uses: [
      'First-line uterotonic for all deliveries',
      'Prevention of postpartum hemorrhage',
      'Treatment of uterine atony'
    ],
    considerations: [
      'FIRST-LINE uterotonic agent',
      'Causes dose-dependent hypotension via vasodilation',
      'Rapid bolus causes profound hypotension - give slowly!',
      'Tachyphylaxis with prolonged use (labor augmentation)',
      'Water retention and hyponatremia with high doses',
      'Coronary vasospasm reported (rare)',
      'Prefer low-dose slow bolus over rapid high-dose',
      'Recommended: 0.5-3 units over 30-60 seconds'
    ],
    bestFor: 'First-line prevention and treatment of uterine atony'
  },
  {
    name: 'Methylergonovine',
    proprietaryName: 'Methergine',
    class: 'uterotonic',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'Serotonin': '++',
      'α-adrenergic': '++'
    },
    onset: 'IV: 1 min, IM: 2-5 min, PO: 10 min',
    duration: '3-4 hours',
    dosing: {
      im: '0.2 mg IM (preferred route)',
      iv: '0.2 mg IV slow push (over 60 sec) - use only if refractory',
      po: '0.2 mg PO q6-8h',
      units: 'mg'
    },
    hemodynamics: {
      HR: '↔',
      BP: '↑↑↑ (vasoconstriction)',
      SVR: '↑↑↑',
      CO: '↔'
    },
    uses: [
      'Second-line uterotonic for atony refractory to oxytocin',
      'Prevention of PPH in high-risk patients',
      'Postpartum uterine massage adjunct'
    ],
    considerations: [
      'Potent vasoconstrictor - causes hypertension',
      'CONTRAINDICATED in hypertension/preeclampsia',
      'CONTRAINDICATED in coronary artery disease',
      'Avoid IV route unless absolutely necessary',
      'May cause severe headache, seizures, MI, stroke',
      'Longer duration than oxytocin',
      'Can cause sustained uterine contraction'
    ],
    bestFor: 'Refractory uterine atony in normotensive patients'
  },
  {
    name: 'Carboprost',
    proprietaryName: 'Hemabate',
    class: 'uterotonic',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'Prostaglandin F2α': '+++'
    },
    onset: 'IM: 15 min',
    duration: '2 hours',
    dosing: {
      im: '250 mcg IM q15-90 min (max 8 doses = 2 mg total)',
      intramyometrial: '250 mcg directly into myometrium',
      units: 'mcg'
    },
    hemodynamics: {
      HR: '↑',
      BP: '↑ or ↓ (variable)',
      PVR: '↑↑ (pulmonary vasoconstriction)',
      SVR: '↑'
    },
    uses: [
      'Refractory uterine atony (after oxytocin fails)',
      'Postpartum hemorrhage'
    ],
    considerations: [
      'Prostaglandin F2α analog',
      'CONTRAINDICATED in asthma (bronchospasm)',
      'Use caution in cardiac/pulmonary disease',
      'Common side effects: N/V, diarrhea, fever, flushing',
      'May increase pulmonary artery pressure',
      'Can cause severe bronchospasm in asthmatics',
      'Very effective but significant side effects',
      'Must give IM or intramyometrial (not IV)'
    ],
    bestFor: 'Refractory atony when oxytocin/methergine ineffective'
  },
  {
    name: 'Misoprostol',
    proprietaryName: 'Cytotec',
    class: 'uterotonic',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'Prostaglandin E1': '+++'
    },
    onset: 'PO/Sublingual: 10-30 min, Rectal: 20-40 min',
    duration: '4-6 hours',
    dosing: {
      rectal: '600-1000 mcg (most common for PPH)',
      sublingual: '400-800 mcg',
      oral: '400-600 mcg',
      units: 'mcg'
    },
    uses: [
      'Prevention and treatment of PPH',
      'Useful when IV access limited',
      'Low-resource settings'
    ],
    considerations: [
      'Prostaglandin E1 analog',
      'Cheapest uterotonic option',
      'No IV formulation - rectal/sublingual/oral only',
      'Common side effects: fever, shivering, diarrhea',
      'Less potent than other uterotonics',
      'Slower onset than IV options',
      'Good option when IV access difficult',
      'WHO recommends 600 mcg sublingual for PPH in low-resource settings'
    ],
    bestFor: 'PPH prevention when IV access unavailable'
  },

  // PREECLAMPSIA MANAGEMENT
  {
    name: 'Magnesium Sulfate',
    proprietaryName: 'Magnesium',
    class: 'preeclampsia',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'NMDA antagonist': '++',
      'Calcium channel': '++'
    },
    onset: 'IV: Immediate',
    duration: '30 minutes (short half-life, renally cleared)',
    dosing: {
      loading: '4-6 g IV over 15-20 min',
      maintenance: '1-2 g/hr continuous infusion',
      units: 'g'
    },
    hemodynamics: {
      HR: '↔',
      BP: '↓ (mild vasodilation)',
      SVR: '↓',
      CO: '↔ or ↓'
    },
    uses: [
      'Seizure prophylaxis in severe preeclampsia',
      'Treatment of eclamptic seizures',
      'Neuroprotection in preterm delivery <32 weeks'
    ],
    considerations: [
      'FIRST-LINE for eclampsia prevention and treatment',
      'Potentiates neuromuscular blockade (↓ dose by 15-25%)',
      'Therapeutic level: 4-8 mEq/L',
      'Toxicity: loss of DTRs (8-12), respiratory depression (12-15), cardiac arrest (>15)',
      'Monitor DTRs, UOP, respiratory rate',
      'Crosses placenta - may cause neonatal hypotonia',
      'Reversal: Calcium gluconate 1 g IV',
      'Renally cleared - adjust in renal insufficiency'
    ],
    bestFor: 'Seizure prophylaxis in severe preeclampsia/eclampsia'
  },
  {
    name: 'Labetalol (Preeclampsia)',
    proprietaryName: 'Trandate',
    class: 'preeclampsia',
    categories: ['obstetric', 'cardiovascular'],
    primaryCategory: 'obstetric',
    receptors: {
      'α1': '++',
      'β1': '+++',
      'β2': '+++'
    },
    onset: '5-10 minutes',
    duration: '3-6 hours',
    dosing: {
      bolus: '10-20 mg IV q10-20 min (escalating: 10, 20, 40, 80 mg)',
      infusion: '0.5-2 mg/min',
      maxDose: '300 mg total',
      units: 'mg'
    },
    hemodynamics: {
      HR: '↔',
      BP: '↓↓',
      SVR: '↓',
      CO: '↔'
    },
    uses: [
      'FIRST-LINE for acute hypertension in preeclampsia',
      'Target: SBP <160, DBP <110',
      'Postpartum hypertension'
    ],
    considerations: [
      'α:β blockade ratio 1:7',
      'Preserves uteroplacental blood flow',
      'Safe in pregnancy',
      'May mask hypoglycemia in diabetics',
      'Contraindicated in asthma, heart block, heart failure',
      'Can cross placenta but minimal neonatal effects',
      'Preferred over hydralazine (fewer side effects)'
    ],
    bestFor: 'First-line acute BP control in preeclampsia'
  },
  {
    name: 'Hydralazine (Preeclampsia)',
    proprietaryName: 'Apresoline',
    class: 'preeclampsia',
    categories: ['obstetric', 'cardiovascular'],
    primaryCategory: 'obstetric',
    receptors: {
      'Direct vasodilator': '+++'
    },
    onset: 'IV: 10-20 min, IM: 20-30 min',
    duration: '4-6 hours',
    dosing: {
      bolus: '5-10 mg IV q20 min (max 20 mg)',
      im: '10 mg IM',
      units: 'mg'
    },
    hemodynamics: {
      HR: '↑↑ (reflex tachycardia)',
      BP: '↓↓',
      SVR: '↓↓',
      CO: '↑'
    },
    uses: [
      'Acute hypertension in preeclampsia (second-line)',
      'Alternative to labetalol'
    ],
    considerations: [
      'Direct arterial vasodilator',
      'Unpredictable BP response',
      'Reflex tachycardia and increased cardiac output',
      'May cause fetal distress (↓ uteroplacental perfusion)',
      'Longer duration of action than labetalol',
      'Associated with more maternal side effects vs labetalol',
      'Can cause severe headache (confounds eclampsia diagnosis)',
      'Now second-line to labetalol in most protocols'
    ],
    bestFor: 'Alternative for acute BP control when labetalol fails/contraindicated'
  },
  {
    name: 'Nifedipine (Preeclampsia)',
    proprietaryName: 'Procardia',
    class: 'preeclampsia',
    categories: ['obstetric'],
    primaryCategory: 'obstetric',
    receptors: {
      'L-type Ca²⁺ channel': '+++'
    },
    onset: 'PO: 10-20 min',
    duration: '4-8 hours (depends on formulation)',
    dosing: {
      immediate: '10 mg PO q20 min (max 3 doses)',
      extended: '30-60 mg PO daily for chronic management',
      units: 'mg'
    },
    hemodynamics: {
      HR: '↑ (reflex)',
      BP: '↓↓',
      SVR: '↓↓',
      CO: '↑'
    },
    uses: [
      'Acute and chronic hypertension in pregnancy',
      'Alternative to labetalol',
      'Tocolysis (off-label)'
    ],
    considerations: [
      'Calcium channel blocker',
      'Oral route only - good for BP control transitioning to outpatient',
      'Potentiates magnesium (risk of hypotension, neuromuscular blockade)',
      'Use caution when combined with magnesium sulfate',
      'May cause flushing, headache',
      'Avoid sublingual (unpredictable absorption)',
      'Safe in pregnancy',
      'Better for chronic vs acute management'
    ],
    bestFor: 'Chronic hypertension management in pregnancy'
  }
];

// Organized exports by class
export const neuraxialOpioids = obMedications.filter(med => med.class === 'neuraxial-opioid');
export const neuraxialAdjuncts = obMedications.filter(med => med.class === 'neuraxial-adjunct');
export const uterotonics = obMedications.filter(med => med.class === 'uterotonic');
export const preeclampsiaDrugs = obMedications.filter(med => med.class === 'preeclampsia');

export default obMedications;
